import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { TextOverflowTooltip } from '@ipfabric/ui-components/Tooltip/TextOverflowTooltip';
import { maybeWrapWithTooltip } from '@ipfabric/ui-components/Tooltip/helpers/maybeWrapWithTooltip';
import classNames from 'classnames';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { FormInputButton } from 'components/FormInputButton.tsx/FormInputButton';
import type { SelectOptionType } from 'components/Select/SelectOption';
import { filterSelectOptions } from 'components/Select/helpers/filterSelectOptions';
import {
  type SelectSearchablePassthruProps,
  useSelectSearchable,
} from 'components/Select/useSelectSearchable';
import { Tag } from 'components/Tag/Tag';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { SyntheticEvent } from 'react';
import {
  type GetSelectOptionValueFunction,
  getSelectOptionValueDefault,
} from './Select';
import './Select.css';
import { horizontalEllipsis } from '@ipfabric/frontend-shared-assets/utils/characters';
import { useClickAway } from '@ipfabric/ui-components/hooks/useClickAway';
import { isAnyEnter } from '@ipfabric/ui-components/utils/keyboard/keyboardKeys';
import type { JSX } from 'react';
import { getSizeCssClass } from 'utils/form/getSizeCssClass';
import {
  type ArrowKeyNavigationMouseEnter,
  arrowKeyNavItemClass,
} from 'utils/keyboard/useArrowKeyNavigation';
import { SelectMenu } from './SelectMenu';

export type MultiSelectProps<TValue> = {
  className?: string;
  value: TValue[];
  placeholder?: string;
  onBlur?: () => void;
  onChange: (value: TValue[]) => void;
  options: SelectOptionType<TValue>[];
  hasError?: boolean;
  size?: 'normal' | 'small' | 'tiny';
  disabledChecked?: boolean;
  onScrollBottom?: () => void;
  loading?: boolean;
  autoFocus?: boolean;
  dataTestId?: string;
} & SelectSearchablePassthruProps &
  (SelectOptionType<TValue> extends { value: string }
    ? { getOptionValue?: GetSelectOptionValueFunction<TValue> }
    : { getOptionValue: GetSelectOptionValueFunction<TValue> });

const multiSelectMsg = {
  selected: 'selected',
};

const selectMsg = {
  placeholder: `Select${horizontalEllipsis}`,
  noOptions: 'No options',
  loading: 'Loading options',
};

export const MultiSelect = <TValue,>({
  getOptionValue = getSelectOptionValueDefault as GetSelectOptionValueFunction<TValue>,
  ...props
}: MultiSelectProps<TValue>): JSX.Element => {
  const [tagsCollapsed, setTagsCollapsed] = useState<boolean>(true);

  const innerRef = useRef<HTMLDivElement>(null);

  const {
    blur,
    focused,
    menuOpen,
    handleChevronClick,
    handleControlClick,
    handleControlFocus,
    handleSearchChange,
    handleSearchKeyDown,
    handleControlKeyDown,
    searchInputRef,
    searchValue,
    setFocused,
    setSearchValue,
  } = useSelectSearchable(props);

  const handleSelect = (option: SelectOptionType<TValue>) => {
    setSearchValue('');
    const filtered = props.value.filter((value) => option.value !== value);
    if (filtered.length === props.value.length) {
      props.onChange([...props.value, option.value]);
    } else {
      props.onChange(filtered);
    }
  };

  const findOption = (value: TValue) =>
    props.options.find(
      (option) => getOptionValue(option.value) === getOptionValue(value),
    );

  const handleClearClick = (e: SyntheticEvent) => {
    e.preventDefault();
    e.stopPropagation();

    props.onChange([]);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: need to run this effect only on mount
  useEffect(() => {
    if (props.autoFocus) {
      setFocused(true);
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional, deps affect size
  useLayoutEffect(() => {
    if (innerRef.current) {
      setTagsCollapsed(
        innerRef.current.offsetWidth < innerRef.current.scrollWidth,
      );
    }
    // recalculate on focused change to reflect potential change of width before
  }, [props.value, focused, props.searchable]);

  const filteredOptions = useMemo(
    () =>
      props.searchable && searchValue
        ? filterSelectOptions(props.options, searchValue)
        : props.options,
    [searchValue, props.searchable, props.options],
  );

  const makeRenderOption =
    (handleOptionMouseEnter: ArrowKeyNavigationMouseEnter) =>
    (option: SelectOptionType<TValue>) => {
      const selected = props.value.some(
        (value) => getOptionValue(value) === getOptionValue(option.value),
      );

      return maybeWrapWithTooltip(
        option.tooltip
          ? {
              content: option.tooltip,
              placement: 'bottom',
            }
          : null,
        getOptionValue(option.value),
      )(
        <div
          key={getOptionValue(option.value)}
          className={classNames(
            'select__option',
            arrowKeyNavItemClass,
            option.disabled && 'select__option--disabled',
            selected && 'select__option--selected',
          )}
          data-testid={`select-option-${option.label}`}
          onClick={() => {
            if (!option.disabled) {
              handleSelect(option);
            }
          }}
          onKeyUp={(e) => {
            if ((isAnyEnter(e.key) || e.key === ' ') && !option.disabled) {
              e.preventDefault();
              handleSelect(option);
            }
          }}
          onMouseEnter={handleOptionMouseEnter}
          tabIndex={-1}
          // biome-ignore lint/a11y/useSemanticElements: This div is used as a custom select option container but is not a semantic list item
          role='listitem'
        >
          <div className='flex items-center justify-between'>
            <div className='select__option-label'>
              <TextOverflowTooltip>{option.label}</TextOverflowTooltip>
            </div>
            <div className='px-2'>
              <Checkbox
                disabled={option.disabled}
                value={selected || !!(option.disabled && props.disabledChecked)}
                onChange={() => handleSelect(option)}
              />
            </div>
          </div>
        </div>,
      );
    };

  const wrapperRef = useClickAway<HTMLDivElement>(blur);

  // render always to keep control focusability consistent,
  // but style like if it wasn't there if not available
  const searchInputAvailable =
    props.searchable && (focused || props.value.length === 0);

  return (
    <div
      className={classNames(
        'select',
        getSizeCssClass(props.size, 'select'),
        props.className,
      )}
      ref={wrapperRef}
      onKeyDown={handleControlKeyDown}
      data-testid={props.dataTestId}
    >
      <div
        className={classNames(
          'select__control',
          props.disabled && 'select__control--disabled',
          props.hasError && 'select__control--error',
          focused && 'select__control--focused',
          menuOpen && 'select__control--open',
        )}
        onClick={handleControlClick}
        onFocus={handleControlFocus}
      >
        <div
          className='relative flex w-full flex-row overflow-x-hidden'
          ref={innerRef}
        >
          <div
            className={classNames(
              'select__control-tags',
              tagsCollapsed && 'invisible absolute',
            )}
          >
            {(() => {
              if (props.value.length > 0) {
                return (
                  props.value
                    .map(findOption)
                    // TS cannot resolve nullable options are filtered out
                    .filter((option): option is SelectOptionType<TValue> => {
                      if (option) {
                        return true;
                      }
                      if (!props.loading) {
                        stumble('Value not found in multi select options');
                      }
                      return false;
                    })
                    .map((option) => (
                      <Tag
                        disabled={props.disabled}
                        className='mr-2'
                        key={getOptionValue(option.value)}
                        onRemove={() => {
                          handleSelect(option);
                        }}
                        size={props.size}
                      >
                        {option.label}
                      </Tag>
                    ))
                );
              }
              if (!props.searchable) {
                // focused searchable input has a placeholder inside the search input
                return (
                  <div className='select__label flex items-center'>
                    {props.placeholder}
                  </div>
                );
              }
              return null;
            })()}
          </div>
          {tagsCollapsed && (
            <Tag className='mr-2' disabled={props.disabled} size={props.size}>
              {props.value.length} {multiSelectMsg.selected}
            </Tag>
          )}

          <input
            className={classNames(
              'select__search-input select__search-input--multiselect',
              // to reasonably use horizontal space (show as much badges as possible when not
              // focused + show enough space for input when focused), input is shown and auto-focused
              // on component focus.
              // Also, always show the input to show placeholder when no items are present
              // (we could use standard placeholder div in that case but that causes subpixel jumps).
              !searchInputAvailable &&
                'w-0 opacity-0 cursor-default pointer-events-none',
            )}
            type='text'
            value={searchValue}
            onChange={handleSearchChange}
            onFocus={() => setFocused(true)}
            onKeyDown={handleSearchKeyDown}
            placeholder={props.value.length === 0 ? props.placeholder : ''}
            ref={searchInputRef}
            disabled={props.disabled}
          />
        </div>
        <div className='flex h-full -mr-3'>
          {props.value.length > 0 && (
            <FormInputButton
              accent='negative'
              disabled={props.disabled}
              onClick={handleClearClick}
              dataTestId='multi-select-clear-button'
            >
              <Icon
                glyph='fa-xmark'
                size='xs'
                onMouseDown={(e) => e.preventDefault()}
              />
            </FormInputButton>
          )}
          <FormInputButton
            className='pr-3 pl-2'
            disabled={props.disabled}
            onClick={handleChevronClick}
          >
            <Icon
              glyph={menuOpen ? 'fa-chevron-up' : 'fa-chevron-down'}
              size='xs'
              onMouseDown={(e) => e.preventDefault()}
            />
          </FormInputButton>
        </div>
      </div>
      {menuOpen && (
        <SelectMenu
          onScrollBottom={props.onScrollBottom}
          wrapperRef={wrapperRef}
          render={(handleOptionMouseEnter: ArrowKeyNavigationMouseEnter) => (
            <>
              {filteredOptions.map(makeRenderOption(handleOptionMouseEnter))}
              {(props.loading || filteredOptions.length === 0) && (
                <div className='select__option select__option-label select__option--no-options'>
                  {props.loading ? selectMsg.loading : selectMsg.noOptions}
                </div>
              )}
            </>
          )}
        />
      )}
    </div>
  );
};
